import { ReactElement } from 'react'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { ApiResponseAction } from '../helpers/action/Action.d'
import { CacheModifySnippetAction, SettingsAction, SettingsInstanceId } from '../helpers/action/SettingsAction.d'
import { BasicContextProvided, BasicContextState } from '../helpers/context.d'

export type SettingsContextProvided = BasicContextProvided<SettingsContextState>

export const INSTANCE_CONTROL_STOPPED = 'stopped'
export const INSTANCE_CONTROL_STARTED = 'started'
export const INSTANCE_CONTROL_STOPPING = 'stopping'
export const INSTANCE_CONTROL_STARTING = 'starting'
export const STATUS_ICON_SUCCESS = 'success'
export const BUTTON_REFRESH = 'refresh'
export const EXTERNAL_API_LINK = 'link'
export const EXTERNAL_API_UNLINK = 'unlink'
export const STATUS_ICON_LOADING = 'loading'
export const STATUS_ICON_FAILED = 'failed'
export const STATUS_ICON_WORKING = 'working'
export const STATUS_PREPARE_PENDING = 'pending'
export const STATUS_ICON_UNAVAILABLE = 'unavailable'
export type InstanceStatusCode = typeof BUTTON_REFRESH
  | typeof EXTERNAL_API_LINK
  | typeof EXTERNAL_API_UNLINK
  | typeof STATUS_ICON_SUCCESS
  | typeof INSTANCE_CONTROL_STOPPED
  | typeof INSTANCE_CONTROL_STARTED
  | typeof INSTANCE_CONTROL_STOPPING
  | typeof INSTANCE_CONTROL_STARTING
  | typeof STATUS_ICON_LOADING
  | typeof STATUS_ICON_WORKING
  | typeof STATUS_ICON_FAILED
  | typeof STATUS_PREPARE_PENDING
  | typeof STATUS_ICON_UNAVAILABLE

type BasicSnippet = {
    /** Only populated/used when modifying the key of a snippet, to prevent delete/create from changing element output order. */
    snippetKey?: string
    /** Only populated/used when displaying a snippet which is waiting for a POST request to complete */
    creating?: boolean
    type: string
}

export type SnippetType = BasicSnippet & {
    str: string
    groupLabel?: string | null
}

export type AttributeType = BasicSnippet & {
    key: string
    value: string
}

export type AliasType = BasicSnippet & {
    canonical: string
    aliases: string[]
    groupLabel?: string | null
}

export type GenericSnippet = SnippetType | AttributeType | AliasType

export type SnippetIdentifier = 'snippets' | 'attributes' | 'aliases'

export type UserSnippets = {
    snippets: {
        [type: string]: {
            [str: string]: SnippetType
        }
    }
    attributes: {
        [type: string]: {
            [key: string]: AttributeType
        }
    }
    aliases: {
        [type: string]: {
            [canonical: string]: AliasType
        }
    }
}

export type SnippetsConfig = Record<SnippetIdentifier, {
    title?: string
    description?: string | ReactElement
    icon?: IconProp
    /** Prompt for adding a new snippet here */
    new: string
    types: Record<string, {
        title: string
        description: string | ReactElement
        /** Input placeholder when creating a new row */
        placeholder: string
        icon?: IconProp
    }>
}>

export type SettingsContextState = BasicContextState & {
    instances: {
        [I in SettingsInstanceId]?: InstanceStatusCode
    }
    /** Governs whether or not followup instance status checks will be sent */
    windowFocused?: boolean
    prepare: PrepareStatus
    snippets?: UserSnippets
    snippetsPending?: {
        modify?: {
            [snippetModifyKey: string]: CacheModifySnippetAction
        }
        create?: {
            [type: string]: {
                [snippetKey: string]: GenericSnippet
            }
        }
    }
    sync?: {
        [api: string]: {
            authenticating?: boolean
            authUrl?: string
            loading?: boolean
            text?: string
        }
    }
}

export type PrepareLog = {
    createdAt?: number
    duration?: number
    estimatedCompletionTime: number
    events?: string[]
    isComplete: boolean
    failed: boolean
    latestEvent?: number
    region: string
    logGroupName: string
    logStreamName: string
}

type PrepareStatus = {
    currentLog?: PrepareLog
    latestCompleteLog?: PrepareLog
    latestStoppedTask?: {
        exitCode?: number
        failed?: boolean
        statusReason?: string
        stoppedAt?: number
        stoppedReason?: string
        taskArn?: string
    }
    latestSuccessfulLog?: PrepareLog
    runningTasks?: []
}

export type CheckPrepareStatusApiResponse = ApiResponseAction & {
    apiResponse: {
        data: PrepareStatus
    }
}

export type InstanceStatus = {
    id: string
    isRunning: boolean
    isStopped: boolean
    isStarting: boolean
    isStopping: boolean
    /** Set when the instance's reference couldn't be resolved (e.g. an environment that isn't deployed). */
    unavailable?: boolean
}
export type CheckInstanceStatusApiResponse = ApiResponseAction & {
    type: 'CHECK_INSTANCE_STATE'
    apiResponse: {
        data: InstanceStatus | Record<string, InstanceStatus>
    }
    sourceAction: SettingsAction
}
